import { useState, useEffect, useMemo } from "react";
import { useWatchlistPush } from "@/demo-guide/useWatchlistPush";
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import CsvUploadLink from "./CsvUploadLink";
import type {
  UseWatchlistCreatorReturn,
  SavedWatchlist,
} from "../hooks/useWatchlistCreator";
import { isPrebuiltWatchlist } from "../prebuiltWatchlists";
import {
  groupWatchlistsByCategory,
  NEWSQUAWK_CATEGORIES,
  type NewsquawkCategory,
} from "../newsquawkCategories";
import { useTagSearch } from "@/shared/api/newsquawk-headlines";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { useEnsureWatchlistAlerts } from "@/shared/hooks/useEnsureWatchlistAlerts";
import NotificationMethodCheckboxes from "@/ui-components/NotificationMethodCheckboxes";
import TermsInput from "./TermsInput";
import QueryBuilderPane from "./QueryBuilderPane";
import { useFilterBuilder, type FilterDraft } from "../hooks/useFilterBuilder";
import { useIsDeskUser } from "@/hooks/useIsDeskUser";
import DeskPublishFields from "./DeskPublishFields";
import PublishConfirmDialog from "./PublishConfirmDialog";

interface WatchlistBuilderPaneProps {
  creator: UseWatchlistCreatorReturn;
  onCancel: () => void;
  /**
   * Fired after a successful save by either route — an ordinary watchlist or
   * a combination — so the host can treat them alike. `saved` is absent only
   * for the guided demo's combination, which writes nothing.
   */
  onSaved?: (saved?: SavedWatchlist) => void;
  /**
   * Hand the part-built combination to the full-screen builder.
   *
   * A handoff, not a nested browser: the host swaps one surface for the other,
   * so the library gets its own three panes and the width they need instead of
   * being folded into a column of this form.
   */
  onOpenAdvanced?: (draft: FilterDraft) => void;
}

export default function WatchlistBuilderPane({
  creator,
  onCancel,
  onSaved,
  onOpenAdvanced,
}: WatchlistBuilderPaneProps) {
  /**
   * Logged-out visitor looking at a list the demo assembled for them.
   *
   * Nothing in this pane can be written — POST /portfolios is 403 — so the
   * headings that promise a save have to say something else.
   */
  const demoPreview = useWatchlistPush();

  const [combineOpen, setCombineOpen] = useState(false);
  const isDeskUser = useIsDeskUser();
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const filterBuilder = useFilterBuilder({
    onSaved: (_name, _categories, id) =>
      onSaved?.(id === undefined ? undefined : { kind: "combined", id }),
    desk: isDeskUser
      ? {
          publishToLibrary: creator.publishToLibrary,
          description: creator.description,
          whenToUse: creator.whenToUse,
          categories: creator.categories,
        }
      : undefined,
  });

  /**
   * Whether this is actually a combination.
   *
   * Decides which store the single Save writes to. A combination genuinely is
   * a different record — it carries operators a flat watchlist cannot express
   * — but that is our concern, not a second button for the user to choose
   * between: they are saving a watchlist either way.
   */
  const submit = async () => {
    // The combined route reports through useFilterBuilder's onSaved above.
    if (isCombining) return filterBuilder.handleSubmit();
    const saved = await creator.handleSubmit();
    if (saved) onSaved?.(saved);
  };

  /**
   * Publishing is the one irreversible step, so it asks first — but only on
   * the way in. Editing a list that is already in the library is an ordinary
   * save and must not nag.
   */
  const handleSave = () => {
    if (holdCopy) return;
    if (isDeskUser && creator.publishToLibrary && !creator.isNewsquawkOwned) {
      setConfirmingPublish(true);
      return;
    }
    submit();
  };

  const isCombining =
    filterBuilder.selectedPortfolioIds.length > 0 ||
    filterBuilder.selectedCategories.length > 0 ||
    filterBuilder.selectedTagsWatchlistIds.length > 0 ||
    filterBuilder.searchKeyword.trim() !== "";

  /** An untouched copy of a Newsquawk list — see copyUnchanged. */
  const holdCopy = creator.copyUnchanged && !isCombining;

  // One name and one set of alerts, owned by this form, mirrored into the
  // combined record so saving either way uses what the user typed once.
  useEffect(() => {
    filterBuilder.setQueryName(creator.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator.name]);
  useEffect(() => {
    filterBuilder.setNotifications(creator.notifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator.notifications]);
  const { ensurePopup, ensureSound } = useEnsureWatchlistAlerts();

  // Mobile detection for showing full import section vs summary chips
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Resolve imported watchlist names for summary chips
  const importedWatchlists =
    creator.mode === "ticker" || creator.mode === "keyword"
      ? creator.portfolios.filter((p) => creator.importedWatchlistIds.has(p.id))
      : [];

  const saving = creator.isSubmitting || filterBuilder.isSubmitting;

  const [combineSearch, setCombineSearch] = useState("");

  const combineCount =
    filterBuilder.selectedPortfolioIds.length +
    filterBuilder.selectedTagsWatchlistIds.length;

  /**
   * The user's own lists, and only those.
   *
   * Combining here is the simple case: things they made, joined with and/or.
   * Offering the desk library too meant 62 rows whose names they had never
   * chosen and whose contents they could not see, which is a browsing problem
   * — and browsing has a surface with room for it. The Advanced Builder holds
   * the full library, so this stays the short list someone recognises.
   *
   * Tag watchlists are included because a list is a list: which store it lives
   * in is our concern, and leaving them out made the inline route unable to
   * express a combination the full builder could.
   */
  const combineRows = useMemo(() => {
    const query = combineSearch.trim().toLowerCase();
    const match = (name: string) =>
      !query || name.toLowerCase().includes(query);

    const rows: {
      key: string;
      name: string;
      on: boolean;
      toggle: () => void;
    }[] = [];

    for (const p of filterBuilder.portfolios) {
      if (p.owner_type === "NEWSQUAWK" || !match(p.name)) continue;
      rows.push({
        key: `p-${p.id}`,
        name: p.name,
        on: filterBuilder.selectedPortfolioIds.includes(p.id),
        toggle: () => filterBuilder.togglePortfolio(p.id),
      });
    }

    for (const w of filterBuilder.tagsWatchlists) {
      if (w.owner_type !== "USER" || !match(w.name)) continue;
      rows.push({
        key: `t-${w.id}`,
        name: w.name,
        on: filterBuilder.selectedTagsWatchlistIds.includes(w.id),
        toggle: () => filterBuilder.toggleTagWatchlist(w.id),
      });
    }

    const collator = new Intl.Collator(undefined, {
      sensitivity: "base",
      numeric: true,
    });
    return rows.sort((a, b) => collator.compare(a.name, b.name));
  }, [
    filterBuilder.portfolios,
    filterBuilder.tagsWatchlists,
    filterBuilder.selectedPortfolioIds,
    filterBuilder.selectedTagsWatchlistIds,
    combineSearch,
  ]);

  /** Entries typed (or imported) into this watchlist directly. */
  const hasTerms =
    creator.tickers.length > 0 ||
    creator.keywords.length > 0 ||
    creator.importedWatchlistIds.size > 0;

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4 space-y-4">
        {/* Import: Mobile gets full browser, desktop gets summary chips */}
        {(creator.mode === "ticker" || creator.mode === "keyword") &&
          (isMobile ? (
            <NewsquawkImportSection creator={creator} />
          ) : importedWatchlists.length > 0 ? (
            <div>
              <label className="text-2xs font-medium text-gray-500 uppercase tracking-wider">
                Imported from Newsquawk
              </label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {importedWatchlists.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded bg-squawk-blue/10 border border-squawk-blue/20 text-xs text-squawk-blue"
                  >
                    <span className="truncate max-w-[160px]">{p.name}</span>
                    <button
                      onClick={() => creator.toggleWatchlistImport(p.id)}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <XMarkIcon className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : null)}

        {/* The name belongs to the watchlist, not to either way of making
            one, so it sits above the fork rather than inside one branch. */}
        {creator.mode !== "tag" && (
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-gray-400">
              Name
            </label>
            <input
              type="text"
              value={creator.name}
              onChange={(e) => creator.setName(e.target.value)}
              placeholder="e.g. My energy book"
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none"
            />
            {creator.errors.name && (
              <p className="mt-1 text-xs text-red-400">{creator.errors.name}</p>
            )}
          </div>
        )}

        {isDeskUser && (
          <DeskPublishFields
            publish={creator.publishToLibrary}
            onPublishChange={creator.setPublishToLibrary}
            description={creator.description}
            onDescriptionChange={creator.setDescription}
            whenToUse={creator.whenToUse}
            onWhenToUseChange={creator.setWhenToUse}
            categories={creator.categories}
            onCategoriesChange={creator.setCategories}
            alreadyPublished={creator.isNewsquawkOwned}
          />
        )}

        {/* With the name, above the fork: colour, priority and alerts belong
            to the watchlist itself, not to either way of building one. Folded
            away so the form still reads as "what am I watching" rather than a
            preferences screen. */}
        <div className="rounded border border-gray-700/50">
          <button
            type="button"
            onClick={() => setOptionsOpen((open) => !open)}
            aria-expanded={optionsOpen}
            className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3 py-2 text-left transition-colors hover:bg-gray-800/60"
          >
            {optionsOpen ? (
              <ChevronDownIcon className="size-4 shrink-0 text-gray-400" />
            ) : (
              <ChevronRightIcon className="size-4 shrink-0 text-gray-400" />
            )}
            <span className="flex-1 text-sm text-gray-300">
              Colour, priority and alerts
            </span>
            <span
              aria-hidden
              className="size-3 rounded-sm border border-gray-600"
              style={{ backgroundColor: creator.color }}
            />
          </button>
          {optionsOpen && (
            <div className="space-y-4 border-t border-gray-700/50 px-3 py-3">
              {/* Color */}
              {creator.mode !== "tag" && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Color
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="relative cursor-pointer">
                      <span
                        className="block size-7 rounded border border-gray-600 hover:border-squawk-blue transition-colors"
                        style={{ backgroundColor: creator.color }}
                      />
                      <input
                        type="color"
                        value={creator.color}
                        onChange={(e) => creator.setColor(e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer size-7"
                      />
                    </label>
                    <input
                      type="text"
                      value={creator.color}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^#[0-9a-fA-F]{0,6}$/.test(v)) creator.setColor(v);
                      }}
                      maxLength={7}
                      className="w-20 px-2 py-1 text-xs font-mono bg-gray-800 text-gray-300 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
                    />
                  </div>
                </div>
              )}

              {/* Priority */}
              {creator.mode !== "tag" && (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                      Priority
                    </span>
                    <p className="text-xs text-gray-600">
                      Lower wins on overlap
                    </p>
                  </div>
                  <select
                    value={creator.priority}
                    onChange={(e) =>
                      creator.setPriority(Number(e.target.value))
                    }
                    className="w-16 px-2 py-1.5 text-sm rounded border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:border-squawk-blue"
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Notifications */}
              <div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  Notifications
                </span>
                <div className="mt-2">
                  <NotificationMethodCheckboxes
                    value={creator.notifications}
                    onChange={creator.setNotifications}
                    comingSoonMethods={
                      creator.mode === "tag" ? ["sound", "popup"] : undefined
                    }
                    onEnableMethod={(method) => {
                      if (method === "sound") ensureSound();
                      if (method === "popup") ensurePopup();
                    }}
                    variant="pane"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Route one: from scratch ──────────────────────────────────── */}
        {creator.mode !== "tag" && (
          <SectionHeading
            // In the demo the list arrives already assembled and is never
            // written anywhere, so "Build from scratch" and the "saving this"
            // badge both describe the opposite of what is happening. The same
            // goes for a copy of a Newsquawk list: nothing here is from
            // scratch, and what gets saved is a new list of their own.
            title={
              demoPreview
                ? "Assembled for you"
                : creator.basedOn
                  ? "Customise this Newsquawk watchlist"
                  : "Build from scratch"
            }
            hint={
              demoPreview
                ? "This is what the builder produced from your description. Nothing is saved in the demo."
                : creator.basedOn
                  ? `Add or remove terms and save your own version. “${creator.basedOn}” itself stays as it is.`
                  : "Type the tickers, companies and topics you want."
            }
            hideSavingBadge={demoPreview || !!creator.basedOn}
            active={hasTerms}
            disabled={isCombining}
            disabledReason="Clear the combination below to type entries instead."
            onClear={creator.clearAll}
          />
        )}

        {/* One field for tickers, companies and topics alike — which of the
            three an entry is comes from the tag lookup, not from a mode the
            user had to pick before typing anything. */}
        {creator.mode !== "tag" && (
          <div
            className={clsx(isCombining && "pointer-events-none opacity-40")}
          >
            {/* CSV sits on the label row: it fills this same field, so it reads
                as another way to use it rather than a step that follows it. */}
            <TermsInput
              creator={creator}
              labelAction={
                creator.isResolvingTerms ? (
                  <span className="text-xs text-gray-500">Looking up…</span>
                ) : (
                  /* Named, because the two CSV routes are not
                     interchangeable: this one files every row as a ticker,
                     which is what makes it match the firehose's ticker field
                     exactly. Uploading a list of topics here would put them
                     somewhere they match less. */
                  <CsvUploadLink
                    onFile={creator.handleCsvUpload}
                    label="Upload tickers CSV"
                    title="Every row is added as a ticker. For topics and phrases, type or paste them into the field instead."
                  />
                )
              }
            />
          </div>
        )}

        {/* Tags section. A copy of a Newsquawk tag watchlist says so, the
            same way the term route above does for a watchlist. */}
        {creator.mode === "tag" && creator.basedOn && (
          <SectionHeading
            title="Customise this Newsquawk watchlist"
            hint={`Add or remove tags and save your own version. “${creator.basedOn}” itself stays as it is.`}
            hideSavingBadge
            active={creator.selectedTags.length > 0}
            disabled={false}
            disabledReason=""
            onClear={creator.clearAll}
          />
        )}
        {creator.mode === "tag" && (
          <TagSelector creator={creator} isMobile={isMobile} />
        )}

        {/* ── Route two: from watchlists you already have ────────────────
            Hidden, not disabled, when the form arrived pre-filled.

            The two routes are a fork for someone starting from nothing. Handed
            a finished list by the concierge — or opening one to edit — the fork
            has already been taken, so a greyed-out half telling them to "clear
            the entries above" is a large dead panel offering to undo the thing
            they came here for. Widening a list that already exists is what
            "Build on it" and the combined builder are for. */}
        {creator.mode !== "tag" && !hasTerms && (
          <>
            <SectionHeading
              title="Build from existing watchlists"
              hint="Combine watchlists you already have, matching on all or any."
              active={isCombining}
              disabled={hasTerms}
              disabledReason="Clear the entries above to combine watchlists instead."
              onClear={filterBuilder.clearAll}
              divider="or"
            />

            <div className={clsx(hasTerms && "pointer-events-none opacity-40")}>
              <div className="flex items-center gap-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Watchlists to combine
                </div>
                {combineCount > 0 && (
                  <span className="font-mono text-2xs tabular-nums text-gray-500">
                    {combineCount} selected
                  </span>
                )}
              </div>

              {/* Kept, though the list is shorter now: people name watchlists
                  for themselves and then have a lot of them. */}
              <div className="relative mt-2">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-500" />
                <input
                  value={combineSearch}
                  onChange={(e) => setCombineSearch(e.target.value)}
                  placeholder="Search watchlists"
                  className="w-full rounded border border-gray-700 bg-gray-800 py-1.5 pl-8 pr-8 text-xs text-gray-200 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none"
                />
                {combineSearch && (
                  <button
                    type="button"
                    onClick={() => setCombineSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer border-none bg-transparent p-0.5 text-gray-500 transition-colors hover:text-gray-300"
                  >
                    <XMarkIcon className="size-3" />
                  </button>
                )}
              </div>

              <div className="mt-2 max-h-44 overflow-y-auto rounded border border-gray-700/50 p-1 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                {combineRows.length === 0 ? (
                  <p className="px-2 py-3 text-center text-xs text-gray-500">
                    {combineSearch
                      ? `No watchlists match “${combineSearch}”.`
                      : "You haven't made any watchlists yet."}
                  </p>
                ) : (
                  combineRows.map((row) => (
                    <label
                      key={row.key}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-300 transition-colors hover:bg-gray-800/60"
                    >
                      <input
                        type="checkbox"
                        checked={row.on}
                        onChange={row.toggle}
                        className="size-3.5 shrink-0 accent-squawk-blue"
                      />
                      <span className="truncate">{row.name}</span>
                    </label>
                  ))
                )}
              </div>

              {isCombining && (
                <div className="mt-4">
                  <QueryBuilderPane
                    builder={filterBuilder}
                    embedded
                    onOpenAdvanced={
                      onOpenAdvanced &&
                      (() =>
                        onOpenAdvanced({
                          name: filterBuilder.queryName,
                          searchKeyword: filterBuilder.searchKeyword,
                          portfolioIds: filterBuilder.selectedPortfolioIds,
                          tagsWatchlistIds:
                            filterBuilder.selectedTagsWatchlistIds,
                          tags: filterBuilder.selectedCategories.map((c) =>
                            String(c.id)
                          ),
                          filterMode: filterBuilder.filterMode,
                          keywordOp: filterBuilder.keywordOp,
                        }))
                    }
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div
        className={clsx(
          "px-4 py-3 border-t border-gray-700/30 flex flex-col sm:flex-row sm:items-center gap-2",
          creator.isEditing ? "sm:justify-between" : "sm:justify-end"
        )}
      >
        {creator.isEditing && (
          <button
            onClick={creator.handleDelete}
            disabled={saving}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded border border-red-800/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors disabled:opacity-50 order-3 sm:order-1"
          >
            Delete
          </button>
        )}
        {/* Held, not hidden, and said why — a greyed button with no reason
            reads as broken. Points at the thing they can already do instead. */}
        {holdCopy && (
          <p className="order-2 text-xs text-gray-500 sm:order-1 sm:mr-auto">
            Change something to save your own version. To use “
            {creator.basedOn}” as it is, pick it from any widget&apos;s filter
            or highlight menu.
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2">
          <button
            onClick={() => {
              // Both routes, or a combination left behind here would decide
              // where the next save went.
              filterBuilder.clearAll();
              onCancel();
            }}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
          >
            Cancel
          </button>
          {/* One button, whichever kind of record this turns out to be. A
              combination is stored as a query because it carries operators a
              flat list cannot express; the user is saving a watchlist either
              way and should not have to know which. */}
          <button
            onClick={handleSave}
            disabled={saving || holdCopy}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? creator.isEditing || creator.basedOn
                ? "Saving..."
                : "Creating..."
              : creator.isEditing
                ? "Save changes"
                : creator.basedOn
                  ? "Save as new watchlist"
                  : "Create watchlist"}
          </button>
        </div>
      </div>

      <PublishConfirmDialog
        open={confirmingPublish}
        listName={creator.name.trim() || "Untitled watchlist"}
        onCancel={() => setConfirmingPublish(false)}
        onConfirm={() => {
          setConfirmingPublish(false);
          submit();
        }}
      />
    </div>
  );
}

// ── Tag Selector (search + browse by category) ──────────────────────────

function TagSelector({
  creator,
  isMobile,
}: {
  creator: UseWatchlistCreatorReturn;
  isMobile: boolean;
}) {
  const {
    search: searchTags,
    tags: searchedTags,
    isLoading: tagSearchLoading,
  } = useTagSearch();
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagSearchedQuery, setTagSearchedQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(menuCategories.map((c) => c.title))
  );

  return (
    <div className="space-y-4">
      {/* Selected tags */}
      {creator.selectedTags.length > 0 && (
        <div>
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Selected ({creator.selectedTags.length})
          </label>
          <div className="mt-1 flex flex-wrap gap-1 max-h-24 overflow-y-auto scrollbar-thin">
            {creator.selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-green-900/20 text-green-300 border border-green-800/30 text-xs"
              >
                {tag.title}
                <button
                  onClick={() => creator.toggleTag(tag)}
                  className="p-0.5 text-green-400 hover:text-green-200"
                >
                  <XMarkIcon className="size-2.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
      {creator.errors.tags && (
        <p className="text-xs text-red-400">{creator.errors.tags}</p>
      )}

      {/* Search */}
      <div>
        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Search for additional tags
        </label>
        <div className="mt-1 flex gap-1.5">
          <input
            value={tagSearchQuery}
            onChange={(e) => setTagSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tagSearchQuery.trim()) {
                e.preventDefault();
                setTagSearchedQuery(tagSearchQuery);
                searchTags(tagSearchQuery);
              }
            }}
            placeholder="Search for tags..."
            className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
          />
          <button
            onClick={() => {
              if (tagSearchQuery.trim()) {
                setTagSearchedQuery(tagSearchQuery);
                searchTags(tagSearchQuery);
              }
            }}
            disabled={!tagSearchQuery.trim() || tagSearchLoading}
            className="px-3 py-1.5 text-xs rounded border border-squawk-blue bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors disabled:opacity-50"
          >
            {tagSearchLoading ? "..." : "Search"}
          </button>
        </div>
        {tagSearchedQuery && tagSearchQuery === tagSearchedQuery && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {searchedTags.length === 0 && !tagSearchLoading && (
              <span className="text-xs text-gray-500">No tags found</span>
            )}
            {searchedTags.map((tag) => {
              const isSelected = creator.selectedTags.some(
                (t) => t.id === tag.tag_id
              );
              return (
                <button
                  key={tag.tag_id}
                  onClick={() =>
                    creator.toggleTag({ title: tag.name, id: tag.tag_id })
                  }
                  className={clsx(
                    "px-2 py-1 text-xs rounded border transition-colors",
                    isSelected
                      ? "bg-squawk-blue/20 text-squawk-blue border-squawk-blue"
                      : "bg-gray-700 text-gray-400 border-gray-600 hover:border-gray-500"
                  )}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Browse by category — mobile only (desktop uses center panel) */}
      {isMobile && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Browse by Category
            </label>
            <button
              onClick={() => {
                if (collapsedSections.size === menuCategories.length)
                  setCollapsedSections(new Set());
                else
                  setCollapsedSections(
                    new Set(menuCategories.map((c) => c.title))
                  );
              }}
              className="text-2xs px-1.5 py-0.5 rounded border border-gray-700 text-squawk-blue hover:border-squawk-blue/50"
            >
              {collapsedSections.size === menuCategories.length
                ? "Expand"
                : "Collapse"}
            </button>
          </div>
          <div className="space-y-1">
            {menuCategories.map((category) => {
              const isCollapsed = collapsedSections.has(category.title);
              return (
                <div
                  key={category.title}
                  className="border border-gray-700/50 rounded overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setCollapsedSections((prev) => {
                        const next = new Set(prev);
                        if (next.has(category.title))
                          next.delete(category.title);
                        else next.add(category.title);
                        return next;
                      })
                    }
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-800/30 hover:bg-gray-800/50 transition-colors focus:outline-none"
                  >
                    {isCollapsed ? (
                      <ChevronRightIcon className="size-3" />
                    ) : (
                      <ChevronDownIcon className="size-3" />
                    )}
                    {category.title}
                  </button>
                  {!isCollapsed && (
                    <div className="p-2 grid grid-cols-2 gap-1 bg-gray-900/20">
                      {category.items.map((catItem) => {
                        const isSelected = creator.selectedTags.some(
                          (t) => t.id === catItem.id
                        );
                        return (
                          <label
                            key={catItem.id}
                            className={clsx(
                              "flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer text-xs transition-colors",
                              isSelected
                                ? "bg-squawk-blue/10 text-squawk-blue"
                                : "text-gray-400 hover:bg-gray-800"
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                creator.toggleTag({
                                  title: catItem.title,
                                  id: catItem.id,
                                })
                              }
                              className="size-3 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="truncate">{catItem.title}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Newsquawk Import Section (for ticker/keyword modes) ────────────────

function NewsquawkImportSection({
  creator,
}: {
  creator: UseWatchlistCreatorReturn;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const newsquawkWatchlists = creator.portfolios.filter((p) => {
    if (!isPrebuiltWatchlist(p)) return false;
    if (creator.mode === "ticker") return p.tickers.length > 0;
    return (p.keywords?.length ?? 0) > 0;
  });

  const grouped = groupWatchlistsByCategory(newsquawkWatchlists);

  if (newsquawkWatchlists.length === 0) return null;

  return (
    <div className="border border-gray-700/50 rounded overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left bg-gray-800/50 hover:bg-gray-800 transition-colors"
      >
        <ChevronRightIcon
          className={clsx(
            "size-3 text-gray-500 transition-transform",
            isOpen && "rotate-90"
          )}
        />
        <BookOpenIcon className="size-3.5 text-squawk-blue/60" />
        <span className="text-xs font-medium text-gray-400">
          Import from Newsquawk
        </span>
        {creator.importedWatchlistIds.size > 0 && (
          <span className="ml-auto text-2xs px-1.5 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue">
            {creator.importedWatchlistIds.size}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="max-h-48 overflow-y-auto border-t border-gray-700/30 scrollbar-thin scrollbar-thumb-gray-700">
          {NEWSQUAWK_CATEGORIES.map((cat) => {
            const catWatchlists = grouped[cat.id as NewsquawkCategory];
            if (!catWatchlists || catWatchlists.length === 0) return null;
            return (
              <div key={cat.id}>
                <div className="px-3 py-1 text-2xs font-semibold uppercase tracking-wider text-gray-500 bg-gray-800/30">
                  {cat.label}
                </div>
                {catWatchlists.map((portfolio) => {
                  const isImported = creator.importedWatchlistIds.has(
                    portfolio.id
                  );
                  return (
                    <label
                      key={portfolio.id}
                      className={clsx(
                        "flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs transition-colors",
                        isImported
                          ? "bg-squawk-blue/5 text-gray-200"
                          : "text-gray-400 hover:bg-gray-800/50"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isImported}
                        onChange={() =>
                          creator.toggleWatchlistImport(portfolio.id)
                        }
                        className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0"
                      />
                      <span className="flex-1 truncate">{portfolio.name}</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A labelled fork in the form.
 *
 * The two ways of making a watchlist — typing entries, or combining lists you
 * already have — produce different records and cannot be mixed: a combination
 * carries operators a flat list cannot express, so saving one silently drops
 * the other. Rather than let both be filled and pick a winner at save time,
 * each route says what it is, marks itself when it is the one in play, and
 * dims when the other is.
 */
function SectionHeading({
  title,
  hint,
  active,
  disabled,
  disabledReason,
  onClear,
  divider = "plain",
  hideSavingBadge = false,
}: {
  title: string;
  hint: string;
  active: boolean;
  disabled: boolean;
  disabledReason: string;
  onClear?: () => void;
  /** Demo: the list is pre-filled and never written, so "saving this" lies. */
  hideSavingBadge?: boolean;
  /** "or" between the two routes; "plain" to separate them from the name. */
  divider?: "plain" | "or";
}) {
  return (
    <div>
      {divider === "or" ? (
        <div
          className="my-7 flex items-center gap-3 text-xs uppercase tracking-[0.1em] text-gray-500"
          aria-hidden
        >
          <span className="h-px flex-1 bg-gray-700/60" />
          or
          <span className="h-px flex-1 bg-gray-700/60" />
        </div>
      ) : (
        <div className="my-6 h-px bg-gray-700/50" aria-hidden />
      )}

      <div className="flex items-center gap-2">
        <h3
          className={clsx(
            "text-sm font-semibold",
            active
              ? "text-squawk-blue"
              : disabled
                ? "text-gray-500"
                : "text-gray-200"
          )}
        >
          {title}
        </h3>
        {active && (
          <>
            {!hideSavingBadge && (
              <span className="rounded bg-squawk-blue/15 px-1.5 py-0.5 text-xs font-semibold text-squawk-blue">
                saving this
              </span>
            )}
            {/* Sits with the badge, where the eye already is — pinned to the
                far right it was easy to miss, and the only other way out of a
                combination was unticking every list one at a time. */}
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                className="shrink-0 cursor-pointer text-xs text-gray-500 transition-colors hover:text-red-400"
              >
                Clear
              </button>
            )}
          </>
        )}
      </div>

      {/* Fixed height: the hint swaps for the disabled reason as you type, and
          a one-to-two-line change here shifted everything below it. */}
      <p className="mt-1 min-h-[1.25rem] text-xs text-gray-500">
        {disabled ? disabledReason : hint}
      </p>
    </div>
  );
}
